import type {jsPDF} from 'jspdf'
import type {FinanceStackOption} from 'slices/RoiCalculator/components/FinanceStackSelect'
import {VOLUME_KEYS} from 'slices/RoiCalculator/constants'
import type {Logo} from 'slices/RoiCalculator/reportLogo'
import {loadLogo} from 'slices/RoiCalculator/reportLogo'
import type {CurrencyCode, Report, RoiCalculatorLabels} from 'slices/RoiCalculator/types'
import {formatCurrency, formatNumber} from 'slices/RoiCalculator/utils'
import {GET_MOSS_HOSTNAME} from 'src/common/constants'

type Color = [number, number, number]

const FOOTER_HEIGHT = 72
const FOOTER_PADDING = 32
const LOGO_WIDTH = 104
const LINK_TEXT = 'getmoss.com'
const LINK_FONT_SIZE = 16
const LINK_COLOR: Color = [0, 36, 20]
const FALLBACK_FILE_NAME = 'moss-roi-estimate'
const FORBIDDEN_FILE_NAME_CHARACTERS = /[\\/:*?"<>|]/g
const CAPTURE_PIXEL_RATIO = 2
const HIDDEN_TEXT_MARGIN = 20
const HIDDEN_TEXT_FONT_SIZE = 12

export type ReportRow = {
  label: string
  value: string
}

export type ReportPdfContent = {
  locale: string
  currency: CurrencyCode
  title: string
  disclaimer: string
  labels: RoiCalculatorLabels
  financeStackOptions: FinanceStackOption[]
}

export const buildReportSections = (
  {input, savings}: Report,
  {locale, currency, labels, financeStackOptions}: ReportPdfContent,
): ReportRow[][] => {
  const financeStackLabel = financeStackOptions.find((option) => option.value === input.stack)?.label ?? ''

  return [
    [
      {label: labels.hoursSaved, value: labels.formatHours(savings.totalHours)},
      {label: labels.moneySaved, value: formatCurrency(savings.money, locale, currency)},
    ],
    VOLUME_KEYS.map((volumeKey) => ({
      label: labels.breakdown[volumeKey],
      value: labels.formatHours(savings.hours[volumeKey]),
    })),
    [
      ...VOLUME_KEYS.map((volumeKey) => ({
        label: labels.volumes[volumeKey],
        value: formatNumber(input.volumes[volumeKey], locale),
      })),
      {label: labels.hourlyRate, value: formatCurrency(input.hourlyRate, locale, currency)},
      {label: labels.minutesPerTransaction, value: formatNumber(input.minutesPerTransaction, locale)},
      {label: labels.financeStack, value: financeStackLabel},
    ],
  ]
}

const isCapturable = (node: HTMLElement): boolean =>
  !(node instanceof HTMLElement && node.dataset.captureExclude !== undefined)

const buildHiddenLines = (sections: ReportRow[][], {title, disclaimer}: ReportPdfContent): string[] =>
  [title, ...sections.flat().map(({label, value}) => `${label} ${value}`), disclaimer].filter(Boolean)

const writeHiddenText = (doc: jsPDF, lines: string[]) => {
  doc.setFontSize(HIDDEN_TEXT_FONT_SIZE)
  doc.text(lines, HIDDEN_TEXT_MARGIN, HIDDEN_TEXT_MARGIN, {renderingMode: 'invisible'})
}

const drawFooter = (doc: jsPDF, pageWidth: number, footerTop: number, logo: Logo | null) => {
  const footerCenter = footerTop + FOOTER_HEIGHT / 2

  if (logo) {
    const logoHeight = (LOGO_WIDTH * logo.height) / logo.width
    const logoTop = footerCenter - logoHeight / 2

    doc.addImage(logo.dataUrl, 'PNG', FOOTER_PADDING, logoTop, LOGO_WIDTH, logoHeight)
    doc.link(FOOTER_PADDING, logoTop, LOGO_WIDTH, logoHeight, {url: GET_MOSS_HOSTNAME})
  }

  doc.setFontSize(LINK_FONT_SIZE)
  doc.setTextColor(...LINK_COLOR)

  doc.textWithLink(
    LINK_TEXT,
    pageWidth - FOOTER_PADDING - doc.getTextWidth(LINK_TEXT),
    footerCenter + LINK_FONT_SIZE / 3,
    {url: GET_MOSS_HOSTNAME},
  )
}

const buildFileName = (title: string): string => {
  const cleanTitle = title.replace(FORBIDDEN_FILE_NAME_CHARACTERS, '').trim()

  return `${cleanTitle || FALLBACK_FILE_NAME}.pdf`
}

export const downloadReportPdf = async (
  report: Report,
  content: ReportPdfContent,
  captureElement: HTMLElement,
): Promise<void> => {
  const [{toPng}, {jsPDF: JsPdf}, logo] = await Promise.all([
    import('html-to-image'),
    import('jspdf'),
    loadLogo().catch(() => null),
  ])

  const {width, height} = captureElement.getBoundingClientRect()
  const capture = await toPng(captureElement, {pixelRatio: CAPTURE_PIXEL_RATIO, cacheBust: true, filter: isCapturable})
  const pageHeight = height + FOOTER_HEIGHT

  const doc = new JsPdf({
    orientation: width > pageHeight ? 'landscape' : 'portrait',
    unit: 'px',
    format: [width, pageHeight],
  })

  doc.addImage(capture, 'PNG', 0, 0, width, height)
  drawFooter(doc, width, height, logo)
  writeHiddenText(doc, buildHiddenLines(buildReportSections(report, content), content))
  doc.save(buildFileName(content.title))
}
